import { useEffect, useState } from "react";
import gearIcon from "../../../../Main/Icons/setting.png";
import PriceSettingsModal from "../../../modals/PriceSettingsModal";
import { BorderedMoneyInput } from "../../../../HouseStyle/Components/Inputs";
import { formatCurrency } from "../../../../../utils";
import ToastError from "../../../../Toasts/ToastError";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";

const PriceBox = ({
  edit,
  prices,
  setPrices,
  selectedTax,
  setSelectedTax,
  priceMargin,
  setPriceMargin,
  setUpdatedTax,
  handleChange,
  product,
  onTaxSettingsChangeCallback,
  refetch,
  allVariations,
  setAllVariations,
  setUpdatedVariations,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [priceModalVisible, setPriceModalVisible] = useState(false);
  const [inclVAT, setInclVAT] = useState(1);
  const [priceChanged, setPriceChanged] = useState(false);
  const [calculatedPrices, setCalculatedPrices] = useState([]);
  const [validationTimeout, setValidationTimeout] = useState(null);
  const [variationBuyInOpen, setVariationBuyInOpen] = useState(false);

  // Initialize calculations on component mount for edit mode
  useEffect(() => {
    if (edit) {
      handleMarginChange();
    }
  }, []);

  useEffect(() => {
    setInclVAT(1);
  }, [refetch]);

  useEffect(() => {
    return () => {
      if (validationTimeout) {
        clearTimeout(validationTimeout);
      }
    };
  }, [validationTimeout]);

  useEffect(() => {
    setCalculatedPrices([]);
    if (prices && prices.length > 0) {
      updateCalculatedPrices();
    }
  }, [prices, selectedTax, inclVAT]);

  useEffect(() => {
    handleMarginChange();
  }, [prices]);

  useEffect(() => {
    if (!prices || prices.length === 0 || selectedTax === null) return;

    let updatedPrices;

    // Calculate prices based on tax inclusion setting
    if (inclVAT === "1" || inclVAT === 1) {
      updatedPrices = calculatePricesFromInclusive(prices, selectedTax);
    } else if (inclVAT === "0" || inclVAT === 0) {
      updatedPrices = calculatePricesFromExclusive(prices, selectedTax);
    } else {
      updatedPrices = prices;
    }

    // Only update if prices actually changed
    if (havePricesChanged(prices, updatedPrices) || !edit) {
      setPrices(updatedPrices);
      if (edit) {
        setUpdatedTax(true);
      }
    }
  }, [selectedTax]);

  // Update calculated prices for display
  const updateCalculatedPrices = () => {
    if (!prices || prices.length === 0) return;

    const formattedPrices = prices.map((price) => ({
      ...price,
      incl_tax:
        typeof price.incl_tax === "string" && price.incl_tax
          ? Number(price.incl_tax.replace(",", ".")).toFixed(2)
          : price.incl_tax
          ? Number(price.incl_tax).toFixed(2)
          : "",
      excl_tax:
        typeof price.excl_tax === "string" && price.excl_tax
          ? Number(price.excl_tax.replace(",", ".")).toFixed(2)
          : price.excl_tax
          ? Number(price.excl_tax).toFixed(2)
          : "",
    }));

    setCalculatedPrices(formattedPrices);
  };

  // Helper function to determine if prices have changed
  const havePricesChanged = (oldPrices, newPrices) => {
    for (let i = 0; i < oldPrices.length; i++) {
      if (
        oldPrices[i].incl_tax !== newPrices[i].incl_tax ||
        oldPrices[i].excl_tax !== newPrices[i].excl_tax
      ) {
        return true;
      }
    }
    return false;
  };

  // Helper function to calculate prices from inclusive values
  const calculatePricesFromInclusive = (priceList, taxRate) => {
    return priceList.map((item) => ({
      ...item,
      excl_tax:
        item.type !== "buy_in_price" && item.incl_tax
          ? (
              parseFloat(item.incl_tax.replace(",", ".")) /
              (1 + taxRate / 100)
            ).toFixed(2)
          : item.excl_tax,
    }));
  };

  // Helper function to calculate prices from exclusive values
  const calculatePricesFromExclusive = (priceList, taxRate) => {
    return priceList.map((item) => ({
      ...item,
      incl_tax: item.excl_tax
        ? (
            parseFloat(item.excl_tax.replace(",", ".")) *
            (1 + taxRate / 100)
          ).toFixed(2)
        : "",
    }));
  };

  // Round input values to 2 decimal places
  const roundToTwoDecimals = (event) => {
    const { name } = event.target;
    const isPriceField =
      (inclVAT === "1" || inclVAT === 1) && name !== "buy_in_price";
    const fieldToUpdate = isPriceField ? "incl_tax" : "excl_tax";

    setPrices((prevPrices) =>
      prevPrices.map((price) => {
        if (price.type === name) {
          const value = price[fieldToUpdate];

          if (value && value !== "") {
            const numValue = parseFloat(String(value).replace(",", "."));
            if (!isNaN(numValue)) {
              return {
                ...price,
                [fieldToUpdate]: numValue.toFixed(2),
              };
            }
          }
        }
        return price;
      })
    );

    if (edit && priceChanged) {
      setUpdatedTax(true);
      setPriceChanged(false);
    }
  };

  // Calculate price margin
  const handleMarginChange = () => {
    if (!prices || prices.length === 0) return;

    const invalidValues = [null, undefined, 0, "", " ", "0", "0.00"];
    const isValid = (value) => !invalidValues.includes(value);
    const buyInPrice =
      prices.find((item) => item.type === "buy_in_price")?.excl_tax ?? null;

    const salePriceItem = prices.find((item) => item.type === "sale_price");
    const standardPriceItem = prices.find((item) => item.type === "price");

    const salePrice = salePriceItem?.excl_tax;
    const standardPrice = standardPriceItem?.excl_tax;

    const price = isValid(salePrice) ? salePrice : standardPrice;

    if (isValid(buyInPrice) && isValid(price)) {
      setPriceMargin((parseFloat(price) - parseFloat(buyInPrice)).toFixed(2));
    } else {
      setPriceMargin(null);
    }
  };

  // Handle price changes for standard and sale prices
  const handlePriceValueChange = async (value, name) => {
    const oldPrice =
      inclVAT === "1" || inclVAT === 1
        ? prices.find((price) => price.type === name)?.incl_tax
        : prices.find((price) => price.type === name)?.excl_tax;

    let inclTax, exclTax;
    const displayValue = value;

    // Calculate the price with and without tax
    if (inclVAT === "1" || inclVAT === 1) {
      inclTax = displayValue;
      exclTax =
        displayValue && displayValue !== ""
          ? (
              parseFloat(String(displayValue).replace(",", ".")) /
              (1 + selectedTax / 100)
            ).toFixed(2)
          : "";
    } else {
      exclTax = displayValue;
      inclTax =
        displayValue && displayValue !== ""
          ? (
              parseFloat(String(displayValue).replace(",", ".")) *
              (1 + selectedTax / 100)
            ).toFixed(2)
          : "";
    }

    // Clear existing validation timeout
    if (validationTimeout) {
      clearTimeout(validationTimeout);
    }

    // Validatie: alleen waarschuwing tonen, niet blokkeren
    if (value && value !== "") {
      const currentValue = parseFloat(String(value).replace(",", "."));
      const isInclusiveTax = inclVAT === "1" || inclVAT === 1;
      const getPrice = (type) => {
        const price = prices.find((p) => p.type === type);
        return price
          ? parseFloat(
              String(isInclusiveTax ? price.incl_tax : price.excl_tax).replace(
                ",",
                "."
              )
            )
          : null;
      };
      const standardPrice = getPrice("price");
      const salePrice = getPrice("sale_price");
      if (
        name === "sale_price" &&
        standardPrice &&
        currentValue >= standardPrice
      ) {
        ToastError("De kortingsprijs moet lager zijn dan de standaard prijs");
      }
      if (name === "price" && salePrice && currentValue <= salePrice) {
        ToastError("De standaard prijs moet hoger zijn dan de kortingsprijs");
      }
      // Optioneel: delay voor extra feedback
      const newTimeout = setTimeout(() => {
        if (
          name === "sale_price" &&
          standardPrice &&
          currentValue >= standardPrice
        ) {
          ToastError("De kortingsprijs moet lager zijn dan de standaard prijs");
        }
        if (name === "price" && salePrice && currentValue <= salePrice) {
          ToastError("De standaard prijs moet hoger zijn dan de kortingsprijs");
        }
      }, 1000);
      setValidationTimeout(newTimeout);
    }

    // Track changes
    if (oldPrice !== value) {
      setPriceChanged(true);
    }

    // Update prices state
    setPrices((prevPrices) => {
      const index = prevPrices.findIndex((e) => e.type === name);
      const newPrices = [...prevPrices];
      
      if (index === -1) {
        // Only add price if it has a value
        if (displayValue !== "") {
          newPrices.push({
            incl_tax: inclTax,
            excl_tax: exclTax,
            type: name,
          });
        }
      } else {
        if (displayValue === "") {
          // Remove the price if empty (allows clearing both standard and discount price)
          if (edit) {
            setUpdatedTax(true);
          }
          return prevPrices.filter((p) => p.type !== name);
        } else {
          newPrices[index].incl_tax = inclTax;
          newPrices[index].excl_tax = exclTax;
        }
      }

      return newPrices;
    });
  };

  // Handle buy-in price changes
  const handleBuyInPriceValueChange = (value, name) => {
    const oldPrice = prices.find((price) => price.type === name)?.excl_tax;

    if (oldPrice !== value) {
      setPriceChanged(true);
    }
    const inclTax =
      value && value !== ""
        ? (
            parseFloat(String(value).replace(",", ".")) *
            (1 + selectedTax / 100)
          ).toFixed(2)
        : "";

    // Don't calculate incl_tax — it's not used or needed for buy_in_price
    const updatedPrice = {
      price_id: prices.find((price) => price.type === name)?.price_id || null,
      type: name,
      incl_tax: inclTax,
      excl_tax: value,
    };

    setPrices((prevPrices) => {
      const priceIndex = prevPrices.findIndex((price) => price.type === name);

      if (priceIndex === -1) {
        return value !== "" ? [...prevPrices, updatedPrice] : prevPrices;
      }

      if (value === "") {
        return prevPrices.filter((p) => p.type !== name);
      }

      return prevPrices.map((price, index) =>
        index === priceIndex ? updatedPrice : price
      );
    });
  };

  // Handle buy-in price changes for individual variations
  const handleVariationBuyInChange = (val, variationId) => {
    const numVal = val ? String(val).replace(',', '.') : '';
    const inclTax = numVal
      ? (parseFloat(numVal) * (1 + (selectedTax || 0) / 100)).toFixed(2)
      : '';

    setAllVariations((prev) =>
      prev.map((v) => {
        if (v.id !== variationId) return v;
        const existingPrices = v.prices ?? [];
        const existingBuyIn = existingPrices.find((p) => p.type === 'buy_in_price');
        const hasBuyIn = !!existingBuyIn;
        if (!numVal) {
          if (existingBuyIn?.price_id) {
            return {
              ...v,
              prices: existingPrices.map((p) =>
                p.type === 'buy_in_price' ? { type: 'buy_in_price', price_id: p.price_id, deleted: true } : p
              ),
            };
          }
          return { ...v, prices: existingPrices.filter((p) => p.type !== 'buy_in_price') };
        }
        return {
          ...v,
          prices: hasBuyIn
            ? existingPrices.map((p) =>
                p.type === 'buy_in_price' ? { ...p, excl_tax: numVal, incl_tax: inclTax } : p
              )
            : [...existingPrices, { price_id: null, type: 'buy_in_price', excl_tax: numVal, incl_tax: inclTax }],
        };
      })
    );
    setUpdatedVariations?.(true);
  };

  // recalculate buy-in price inclTax when tax rate changes
  useEffect(() => {
    // Skip if there are no prices or we're still loading
    if (!prices || prices.length === 0) return;

    const buyInPriceItem = prices.find((item) => item.type === "buy_in_price");
    if (buyInPriceItem && buyInPriceItem.excl_tax) {
      handleBuyInPriceValueChange(buyInPriceItem.excl_tax, "buy_in_price");
    }
  }, [selectedTax]);

  // Handle tax inclusion setting change
  const handleTaxChange = (event) => {
    const newInclVAT = event.target.value;
    setInclVAT(newInclVAT);

    if (!edit) {
      const formattedPrices = prices.map((curr) => ({
        price_id: curr.price_id,
        type: curr.type,
        incl_tax:
          typeof curr.incl_tax === "string"
            ? Number(curr.incl_tax.replace(",", ".")).toFixed(2)
            : Number(curr.incl_tax).toFixed(2),
        excl_tax:
          typeof curr.excl_tax === "string"
            ? Number(curr.excl_tax.replace(",", ".")).toFixed(2)
            : Number(curr.excl_tax).toFixed(2),
      }));

      setPrices(formattedPrices);
    }
  };

  const getPriceDisplayValue = (priceType) => {
    const price = prices.find((item) => item.type === priceType);
    if (!price) return "";

    // Always use excl_tax for buy-in price
    if (priceType === "buy_in_price") {
      return typeof price.excl_tax === "string"
        ? price.excl_tax.replace(",", ".")
        : price.excl_tax;
    }

    const value =
      inclVAT === "1" || inclVAT === 1 ? price?.incl_tax : price?.excl_tax;
    return typeof value === "string" ? value.replace(",", ".") : value;
  };

  const getOppositeTaxPrice = (priceType) => {
    const price =
      calculatedPrices.find((item) => item.type === priceType) ||
      prices.find((item) => item.type === priceType);
    if (!price) return "";
    return inclVAT === "1" || inclVAT === 1
      ? price?.excl_tax || ""
      : price?.incl_tax || "";
  };

  const getTaxLabel = () => {
    return inclVAT === "1" || inclVAT === 1 ? "Excl. BTW" : "Incl. BTW";
  };

  // Get current tax percentage for display
  const getCurrentTaxPercentage = () => {
    return selectedTax ? `${selectedTax}%` : "0%";
  };

  return (
    <>
      <PriceSettingsModal
        setPriceModalVisible={setPriceModalVisible}
        priceModalVisible={priceModalVisible}
        setSelectedTax={setSelectedTax}
        product={product}
        refetch={refetch}
        edit={edit}
        onTaxSettingsChangeCallback={onTaxSettingsChangeCallback}
        setIsLoading={setIsLoading}
      />
      <div
        className="whiteBox boxPrices p-5 h-100"
      >
        <div className="d-flex flex-wrap justify-content-between mb-2 mb-md-4 gap-2 pricesHeader">
          <h4 className="fw-bold m-0">Prijzen</h4>
          <div className="d-flex md:flex-row align-items-center pricesControls">
            <select
              className="smallStandardInput standardDropdownArrow me-2 pe-4"
              onChange={handleTaxChange}
              value={inclVAT}
            >
              <option value={0}>Prijzen excl. BTW</option>
              <option value={1}>Prijzen incl. BTW</option>
            </select>

            <button
              className="rounded-pill px-2 text-secondary gearButtonPrices d-flex align-items-center h-100"
              type="button"
              onClick={() => setPriceModalVisible(true)}
            >
              <img src={gearIcon} height="17px" width="17px" alt="Settings" />
            </button>
          </div>
        </div>

        {isLoading && edit ? (
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="">
            <div className="d-flex flex-column">
              <div className="d-flex justify-content-end mb-2">
                <small className="text-muted" style={{ fontSize: '12px' }}>
                  BTW: {getCurrentTaxPercentage()}
                </small>
              </div>
              <div className="row g-3 mb-md-3">
                {/* Standard price */}
                <div className="col-6">
                  <label className="fw-medium mb-2" style={{ fontSize: '14px' }}>Standaard prijs</label>
                  <BorderedMoneyInput
                    size="m"
                    restrictMinus={true}
                    decimalsLimit={2}
                    inputValue={getPriceDisplayValue("price")}
                    setInputValue={(val) =>
                      handlePriceValueChange(val, "price")
                    }
                    updateFunction={edit ? handleChange : undefined}
                    onBlur={(e) => roundToTwoDecimals(e)}
                    updateWaitTimer={null}
                  />
                  <p className="bottomText fw-medium" style={{ fontSize: '11px' }}>
                    {formatCurrency(getOppositeTaxPrice("price"))} {getTaxLabel()}
                  </p>
                </div>

                {/* Discount price */}
                <div className="col-6">
                  <label className="fw-medium mb-2" style={{ fontSize: '14px' }}>Kortingsprijs</label>
                  <BorderedMoneyInput
                    size="m"
                    restrictMinus={true}
                    decimalsLimit={2}
                    inputValue={getPriceDisplayValue("sale_price")}
                    setInputValue={(val) =>
                      handlePriceValueChange(val, "sale_price")
                    }
                    updateFunction={edit ? handleChange : undefined}
                    onBlur={(e) => roundToTwoDecimals(e)}
                    updateWaitTimer={null}
                  />
                  <p className="bottomText fw-medium" style={{ fontSize: '11px' }}>
                    {formatCurrency(getOppositeTaxPrice("sale_price"))} {getTaxLabel()}
                  </p>
                </div>
              </div>

              <div className="row g-3">
                {/* Buy-in price */}
                <div className="col-6">
                  <label className="fw-medium mb-2" style={{ fontSize: '14px' }}>
                    Inkoopprijs excl. BTW
                  </label>
                  <BorderedMoneyInput
                    size="m"
                    restrictMinus={true}
                    minAmount={0}
                    decimalsLimit={2}
                    inputValue={getPriceDisplayValue("buy_in_price")}
                    setInputValue={(val) =>
                      handleBuyInPriceValueChange(val, "buy_in_price")
                    }
                    updateWaitTimer={null}
                    updateFunction={edit ? handleChange : undefined}
                  />
                </div>

                {/* Margin */}
                <div className="col-6">
                  <label className="fw-medium mb-2" style={{ fontSize: '14px' }}>Marge excl. BTW</label>
                  <BorderedMoneyInput
                    size="m"
                    restrictMinus={true}
                    minAmount={0}
                    decimalsLimit={2}
                    inputValue={
                      priceMargin != null && !isNaN(priceMargin)
                        ? priceMargin
                        : null
                    }
                  />
                </div>
              </div>

            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PriceBox;
